/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Notifications logic module.
 * Handles notifications data retrieval, filtering, sorting, and pagination operations.
 * @module logic/notifications
 */

import { getToken, refreshToken, signOut } from "../../utils/common.js";
import {
  assertAuthorizedApiResponse,
  parseExportDownloadUrl,
} from "../../utils/eventApi.js";

const HTTP_NOT_FOUND = 404;

/** Default response when the Notifications page aggregate endpoint fails or is unavailable. */
const NOTIFICATIONS_PAGE_DEFAULT_RESPONSE = {
  notifications: [],
  lastEvaluatedKey: null,
};

export const initializeNotificationFormPage = async (
  userId,
  changePermissions,
  getSectors,
  setSectors,
  setLoading,
  navigate,
) => {
  const token = localStorage.getItem("token");
  if (!token || token.trim() === "undefined") {
    return;
  }
  setLoading(true);
  await getUserRoleData(userId, changePermissions, navigate);
  const sectors = await getSectors(navigate);
  setSectors(sectors);
  setLoading(false);
};

/**
 * Fetches and sets user role/permission data.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} changePermissions - Callback to update permissions state
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const getUserRoleData = async (userId, changePermissions, navigate) => {
  const tokenToUse = localStorage.getItem("token");
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${tokenToUse}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
      options,
    );
    const roleData = await response.json();
    const isTokenExpired =
      roleData?.message === TOKEN_EXPIRED_MESSAGE ||
      roleData?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE;
    if (isTokenExpired) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await getUserRoleData(userId, changePermissions, navigate);
      return;
    }
    if (response.ok && roleData?.permisos != null) {
      changePermissions(roleData.permisos);
    }
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
  }
};

/** Builds page-data URL with optional lastKey for pagination. */
const buildPageDataUrl = (lastKey) => {
  const base = `${process.env.REACT_APP_ROOT_API}customNotification/page-data`;
  if (!lastKey) {
    return base;
  }
  // Backend returns lastEvaluatedKey already URL-encoded; send as-is to avoid double-encoding.
  const keyParam =
    typeof lastKey === "string"
      ? lastKey
      : encodeURIComponent(JSON.stringify(lastKey));
  return `${base}?lastKey=${keyParam}`;
};

/**
 * Fetches the first page of data for the Notifications page: notifications list (paginated),
 * and lastEvaluatedKey for load-more.
 *
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<{ notifications: Array, lastEvaluatedKey: * }>}
 */
export const initializeNotificationsPage = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(buildPageDataUrl(null), options);
    const data = await response.json();
    logger.info("data: ", { data: data });

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return NOTIFICATIONS_PAGE_DEFAULT_RESPONSE;
      }
      return await initializeNotificationsPage(navigate);
    }

    return {
      notifications: Array.isArray(data.items) ? data.items : [],
      lastEvaluatedKey: data.lastEvaluatedKey ?? null,
    };
  } catch (error) {
    logger.error("Error initializing Notifications page:", {
      err: sanitizeError(error),
    });
    return NOTIFICATIONS_PAGE_DEFAULT_RESPONSE;
  }
};

/**
 * Fetches the next page of notifications using the last evaluated key.
 * Use when the user requests "load more" (e.g. next page beyond currently loaded data).
 *
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string|Object} lastKey - Last evaluated key from the previous page response
 * @returns {Promise<{ users: Array, companies: Object, lastEvaluatedKey: * }>}
 */
export const fetchMoreNotificationsPage = async (navigate, lastKey) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(buildPageDataUrl(lastKey), options);
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return { notifications: [], lastEvaluatedKey: null };
      }
      return await fetchMoreNotificationsPage(navigate, lastKey);
    }

    return {
      notifications: Array.isArray(data.notifications)
        ? data.notifications
        : [],
      lastEvaluatedKey: data.lastEvaluatedKey ?? null,
    };
  } catch (error) {
    logger.error("Error fetching more notifications:", {
      err: sanitizeError(error),
    });
    return { notifications: [], lastEvaluatedKey: null };
  }
};

/**
 * Sorts an array of notifications by subject according to the sort configuration.
 * @param {Array<Object>} notifications - Array of notification objects to sort
 * @param {Object} sortConfig - Sort configuration object
 * @param {string} sortConfig.direction - Sort direction ('ascending' or 'descending')
 * @returns {Array<Object>} Sorted array of notifications
 */
export const sortNotifications = (notifications, sortConfig) => {
  if (!Array.isArray(notifications) || notifications.length === 0) {
    return [];
  }

  return [...notifications].sort((a, b) => {
    const subjectA = a.asunto ? a.asunto.toLowerCase() : "";
    const subjectB = b.asunto ? b.asunto.toLowerCase() : "";
    if (subjectA < subjectB) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (subjectA > subjectB) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Filters notifications by subject based on a search query.
 * @param {Array<Object>} notifications - Array of notification objects to filter
 * @param {string} searchQuery - Search string to filter by
 * @returns {Array<Object>} Filtered array of notifications matching the search query
 */
export const filterNotifications = (notifications, searchQuery) => {
  if (!searchQuery) {
    return notifications;
  }
  return notifications.filter(
    (notification) =>
      notification.asunto?.toLowerCase().includes(searchQuery.toLowerCase()) ??
      false,
  );
};

/**
 * Paginates an array of notifications for display.
 * @param {Array<Object>} notifications - Array of notification objects to paginate
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} resultsLimit - Number of results per page
 * @returns {Array<Object>} Subset of notifications for the current page
 */
export const paginateNotifications = (
  notifications,
  currentPage,
  resultsLimit,
) => {
  return notifications.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );
};

/**
 * Creates a user via API. Handles token expiry with refresh and retry.
 * On success calls onSuccess with the created user (including nombreRol).
 * On conflict sets RFC or email error via callbacks.
 *
 * @async
 * @param {Object} notificationData - Notification payload (tipoDeUsuario, nombre, titulo, correoElectronico, etc.)
 * @param {Object} callbacks
 * @param {Function} callbacks.navigate - React Router navigation function
 * @param {Function} callbacks.onSuccess - Called with created notification
 * @returns {Promise<{ success: boolean }?>} success true when notification was created; undefined on redirect/error
 */
export const createAndSendCustomNotification = async (
  notificationData,
  {
    navigate,
    onSuccess,
    onError,
    onCompaniesError,
    onUsersError,
    onPhoneNumbersError,
  },
) => {
  const authToken = getToken();
  if (!authToken) {
    logger.error("Failed to get the token");
    return { success: false };
  }

  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(notificationData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}customNotification/send`,
      options,
    );

    if (response.ok) {
      const responseData = await response.json();
      const newNotification = responseData.notification;
      onSuccess(newNotification);
      return { success: true };
    }

    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return { success: false };
      }
      return await createAndSendCustomNotification(notificationData, {
        navigate,
        onSuccess,
        onError,
      });
    }

    if (response.status === HTTP_NOT_FOUND && errorData?.code === "NO_COMPANIES") {
      onCompaniesError?.();
    } else if (response.status === HTTP_NOT_FOUND && errorData?.code === "NO_USERS") {
      onUsersError?.();
    } else if (
      response.status === HTTP_NOT_FOUND &&
      errorData?.code === "NO_PHONE_NUMBERS"
    ) {
      onPhoneNumbersError?.();
    } else {
      onError?.(errorData);
    }
    return { success: false };
  } catch (error) {
    logger.error("Error creating and sending custom notification:", {
      err: sanitizeError(error),
    });
    return { success: false };
  }
};

export const downloadNotificationFile = async (notificationId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}customNotification/${notificationId}/recipients-file-url`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await downloadNotificationFile(notificationId, navigate);
    }

    await assertAuthorizedApiResponse(response, data, navigate);
    if (!response.ok) {
      return null;
    }
    return parseExportDownloadUrl(data);
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Unsubscribes the user from Custom Notification emails using a signed token (public).
 * @param {string} token
 * @returns {Promise<{ok: boolean, code?: string, message?: string}>}
 */
export const unsubscribeFromCustomEmails = async (token) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}customNotification/unsubscribe?token=${encodeURIComponent(token)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": CONTENT_TYPE_JSON,
        },
      },
    );
    const data = await response.json();
    return {
      ok: response.ok,
      code: data?.code,
      message: data?.message,
    };
  } catch (error) {
    logger.error("Error unsubscribing from custom emails:", {
      err: sanitizeError(error),
    });
    return { ok: false, code: "REQUEST_FAILED", message: error.message };
  }
};
